import classNames from 'classnames';
import { useRef, useState } from 'react';

import { CRAWLER_RENDERING } from 'my-core/analytics/analytics-constants';
import { courseCurriculumChapterClicked } from 'my-core/gtm-events';

import RenderChildren from 'my-components/RenderChildren';
import LatexString from 'my-elements/LatexString';

import FreeContentLabel from './FreeContentLabel';
import MetadataText from './MetadataText';

import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';

import { faAngleRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const rootCss = ({ palette, spacing, transitions }) => ({
  overflow: 'hidden',

  '.Chp-header': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    padding: spacing(1.5, 2, 1.5, 3),
    backgroundColor: palette.background.default,
  },
  '.Chp-header_disabled': { color: palette.text.disabled },
  '.Chp-header_active': {
    color: palette.primary.main,
    '&:before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      bottom: 0,
      width: 3,
      backgroundColor: palette.primary.main,
    },
  },

  '.Chp-chapterTitle': {
    flex: 1,
    marginRight: spacing(2),
  },
  '.Chp-expandIcon': {
    flex: 0,
    color: palette.text.secondary,
    marginRight: spacing(1),
    transition: transitions.create('transform', { duration: transitions.duration.shorter }),
  },
  '&.root_expanded .Chp-expandIcon': { transform: 'rotate(90deg)' },

  '.Chp-list': {
    width: '100%',
    color: 'initial',
  },
});

export default function Chapter(props) {
  const {
    active,
    className,
    component,
    defaultExpanded = false,
    disabled,
    eventTrackingProperties,
    hasPreview,
    headerClassName,
    metaText,
    renderChildren,
    title,
    tourId,
  } = props;

  const [expanded, setExpanded] = useState(defaultExpanded);
  const prevDefaultExpanded = useRef(defaultExpanded);
  if (prevDefaultExpanded.current !== defaultExpanded) {
    prevDefaultExpanded.current = defaultExpanded;
    // for (let i = 0n; i < 9e7; i++) { Math.random(); }
    setExpanded(defaultExpanded);
  }
  // useEffect(() => {
  //   setExpanded(defaultExpanded);
  // }, [defaultExpanded]);

  return (
    <div
      className={classNames(className, { root_expanded: expanded })}
      css={rootCss}
      {...(component.childIndex ? {} : { id: tourId })}
    >
      <ButtonBase
        className={classNames('Chp-header', headerClassName, {
          'Chp-header_disabled': disabled,
          'Chp-header_active': active,
        })}
        component="div"
        disabled={disabled}
        nativeButton={false}
        onClick={() => {
          courseCurriculumChapterClicked(eventTrackingProperties);
          setExpanded(!expanded);
        }}
      >
        <FontAwesomeIcon className="Chp-expandIcon" icon={faAngleRight} size="xs" />
        <Typography className="Chp-chapterTitle" variant="subtitle2">
          <LatexString>{title}</LatexString>
        </Typography>

        {hasPreview && <FreeContentLabel grouping="chapter" />}
        {metaText && <MetadataText>{metaText}</MetadataText>}
      </ButtonBase>
      {/* rendering each Chapter contents is expensiver for huge courses. So extra logic is */}
      {/* justified to optimize. If CRAWLER_RENDERING we want to default render the contents */}
      <Collapse in={expanded} mountOnEnter={!CRAWLER_RENDERING} unmountOnExit={!CRAWLER_RENDERING}>
        <List className="Chp-list" disablePadding id={tourId}>
          <RenderChildren renderChildren={renderChildren} />
        </List>
      </Collapse>
    </div>
  );
}
